/**
 * DvfMap — orthophoto IGN + the subject parcel + the cadastral parcels of
 * each DVF comparable, color-coded by their €/m² vs. the headline median.
 *
 * Design intent :
 *   - Reuse the SatelliteMap pattern (single WMS GetMap, no tile stitching)
 *     so we stay under ~400 kB per fiche view and don't add a map library.
 *   - Draw the exact DVF parcels (from apicarto via getParcelGeometries)
 *     rather than approximate circles. When a parcel can't be resolved
 *     (very rare — cadastre remap), we fall back to a centroid pin so the
 *     user still sees the transaction on the map.
 *   - Color scale is relative to the median shown in the header : four
 *     buckets ("bien en-dessous", "sous la médiane", "au-dessus", "bien
 *     au-dessus"). Keeps the map readable without a legend lookup.
 *   - Click a parcel → a selection highlight so the user can cross-read
 *     the map and the comparables table.
 */

import { useMemo, useState } from "react";
import { Pill } from "./ui";
import { Ic } from "./icons";
import {
  useParcelGeometries,
  type ParcelGeom,
} from "../lib/cadastreGeom";
import type { CadastreParcel, DvfTransaction } from "../lib/types";

// Match SatelliteMap so the two visual frames are interchangeable.
const VIEW_W = 600;
const VIEW_H = 400;
const TARGET_METRIC_RATIO = VIEW_W / VIEW_H;

// Extra padding around the aggregated bbox. Breathes enough that the outer
// parcels don't kiss the edge, without making the subject tiny.
const PADDING_M = 50;

const WMS_URL = "https://data.geopf.fr/wms-r/wms";

type AnyGeom =
  | { type: "Polygon"; coordinates: number[][][] }
  | { type: "MultiPolygon"; coordinates: number[][][][] }
  | null
  | undefined;

type Bbox = { minLng: number; minLat: number; maxLng: number; maxLat: number };

// Price-vs-median bucket. Tuned so the neutral band covers the typical
// ±20 % spread around the trimmed median — meaning most comparables fall
// in the two middle buckets, and the colored extremes draw the eye.
type PriceBucket = "low" | "mid-low" | "mid-high" | "high" | "unknown";

function bucketFor(pricePerM2: number | null, median: number | null): PriceBucket {
  if (!pricePerM2 || !median) return "unknown";
  const ratio = pricePerM2 / median;
  if (ratio < 0.8) return "low";
  if (ratio < 1.0) return "mid-low";
  if (ratio < 1.2) return "mid-high";
  return "high";
}

// Palette tuned for visibility over an orthophoto background : cool colors
// for below-median, warm for above. Stroke is opaque, fill is translucent
// so the orthophoto reads through.
const BUCKET_STYLE: Record<
  PriceBucket,
  { fill: string; stroke: string; label: string }
> = {
  low: {
    fill: "rgba(59,130,246,0.40)",
    stroke: "#1D4ED8",
    label: "Bien en-dessous",
  },
  "mid-low": {
    fill: "rgba(125,211,252,0.45)",
    stroke: "#0284C7",
    label: "Sous la médiane",
  },
  "mid-high": {
    fill: "rgba(253,224,71,0.45)",
    stroke: "#CA8A04",
    label: "Au-dessus",
  },
  high: {
    fill: "rgba(244,114,182,0.45)",
    stroke: "#BE185D",
    label: "Bien au-dessus",
  },
  unknown: {
    fill: "rgba(156,163,175,0.35)",
    stroke: "#4B5563",
    label: "Prix inconnu",
  },
};

export function DvfMap({
  subject,
  transactions,
  medianPricePerM2,
  basis,
  className = "",
}: {
  subject: CadastreParcel;
  transactions: DvfTransaction[];
  medianPricePerM2: number | null;
  basis: "bati" | "terrain" | null;
  className?: string;
}) {
  // Collect parcel ids across all comparables and resolve in one batch.
  const idpars = useMemo(
    () => transactions.flatMap((t) => t.parcelIds ?? []),
    [transactions],
  );
  const geomsState = useParcelGeometries(idpars);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const resolvedGeoms: Record<string, ParcelGeom | null> =
    geomsState.status === "ready" ? geomsState.geometries : {};

  // Pair each transaction with the best parcel geometry we have. If several
  // parcels resolve, we keep them all (they'll all get drawn together with
  // the same color — treating the mutation as a single visual unit).
  const txViews = useMemo(() => {
    return transactions.map((t) => {
      const geoms = (t.parcelIds ?? [])
        .map((id) => resolvedGeoms[id.toUpperCase()])
        .filter((g): g is ParcelGeom => !!g && !!g.geometry);
      const pricePerM2 =
        basis === "terrain" ? t.price_per_m2_terrain : t.price_per_m2_bati;
      return {
        tx: t,
        geoms,
        pricePerM2,
        bucket: bucketFor(pricePerM2, medianPricePerM2),
      };
    });
  }, [transactions, resolvedGeoms, basis, medianPricePerM2]);

  // Build the aggregate bbox : subject parcel + resolved parcels + centroids
  // of transactions we couldn't resolve (so the fallback pins stay on-map).
  const view = useMemo(() => {
    const geoms: AnyGeom[] = [subject.geometry as AnyGeom];
    const pts: Array<[number, number]> = [];
    for (const v of txViews) {
      if (v.geoms.length > 0) {
        for (const g of v.geoms) geoms.push(g.geometry as AnyGeom);
      } else {
        pts.push([v.tx.lng, v.tx.lat]);
      }
    }
    const bb = bboxOfAll(geoms, pts);
    if (!bb) return null;
    const padded = expandBbox(bb, PADDING_M);
    const fitted = fitToMetricRatio(padded, TARGET_METRIC_RATIO);
    const project = (lng: number, lat: number): [number, number] => [
      ((lng - fitted.minLng) / (fitted.maxLng - fitted.minLng)) * VIEW_W,
      ((fitted.maxLat - lat) / (fitted.maxLat - fitted.minLat)) * VIEW_H,
    ];
    const bboxParam = [
      fitted.minLat,
      fitted.minLng,
      fitted.maxLat,
      fitted.maxLng,
    ]
      .map((n) => n.toFixed(6))
      .join(",");
    const qs = new URLSearchParams({
      SERVICE: "WMS",
      REQUEST: "GetMap",
      VERSION: "1.3.0",
      LAYERS: "ORTHOIMAGERY.ORTHOPHOTOS",
      STYLES: "normal",
      CRS: "EPSG:4326",
      BBOX: bboxParam,
      WIDTH: String(VIEW_W * 2),
      HEIGHT: String(VIEW_H * 2),
      FORMAT: "image/jpeg",
    });
    return { project, imageUrl: `${WMS_URL}?${qs.toString()}` };
  }, [subject.geometry, txViews]);

  if (!view) {
    return (
      <div
        className={`relative rounded-xl2 overflow-hidden bg-ink-50 flex items-center justify-center ${className}`}
        style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}` }}
      >
        <div className="text-[11.5px] text-ink-500">
          Aucune géométrie à afficher.
        </div>
      </div>
    );
  }

  const subjectPath = geomToSvgPath(subject.geometry as AnyGeom, view.project);

  // Count resolved vs. fallback so we can surface a small status line if
  // some parcels fell back to centroid pins.
  const resolvedCount = txViews.filter((v) => v.geoms.length > 0).length;
  const fallbackCount = txViews.length - resolvedCount;

  return (
    <div
      className={`relative rounded-xl2 overflow-hidden hairline bg-ink-900 ${className}`}
    >
      <img
        src={view.imageUrl}
        alt="Carte des comparables DVF"
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.opacity = "0";
        }}
      />
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full"
      >
        {/* Subject parcel — always on top of comparable fills but below the
            selected-transaction stroke. Lime matches the SatelliteMap. */}
        <path
          d={subjectPath}
          fill="rgba(217,234,44,0.22)"
          stroke="#D9EA2C"
          strokeWidth="2.2"
          strokeLinejoin="round"
          style={{ filter: "drop-shadow(0 0 2px rgba(0,0,0,0.55))" }}
        />

        {/* Comparable parcels */}
        {txViews.map((v) => {
          const style = BUCKET_STYLE[v.bucket];
          const isSelected = selectedId === v.tx.id;
          if (v.geoms.length === 0) {
            // Centroid-pin fallback. Radius proportional to price bucket so
            // the visual hierarchy still carries even without polygons.
            const [cx, cy] = view.project(v.tx.lng, v.tx.lat);
            return (
              <g
                key={v.tx.id}
                onClick={() =>
                  setSelectedId(isSelected ? null : v.tx.id)
                }
                style={{ cursor: "pointer" }}
              >
                <circle
                  cx={cx}
                  cy={cy}
                  r={isSelected ? 10 : 7}
                  fill={style.fill}
                  stroke={style.stroke}
                  strokeWidth={isSelected ? 2.2 : 1.4}
                />
              </g>
            );
          }
          return (
            <g
              key={v.tx.id}
              onClick={() =>
                setSelectedId(isSelected ? null : v.tx.id)
              }
              style={{ cursor: "pointer" }}
            >
              {v.geoms.map((g, i) => (
                <path
                  key={i}
                  d={geomToSvgPath(g.geometry as AnyGeom, view.project)}
                  fill={style.fill}
                  stroke={style.stroke}
                  strokeWidth={isSelected ? 2.4 : 1.4}
                  strokeLinejoin="round"
                  style={
                    isSelected
                      ? { filter: "drop-shadow(0 0 3px rgba(0,0,0,0.65))" }
                      : undefined
                  }
                />
              ))}
            </g>
          );
        })}
      </svg>

      {/* Attribution */}
      <div className="absolute bottom-2 right-2 text-[9px] font-mono uppercase tracking-widest text-white/85 bg-black/45 px-1.5 py-0.5 rounded">
        © IGN — DGFiP DVF
      </div>

      {/* Top-left: subject marker */}
      <div className="absolute top-3 left-3">
        <Pill tone="soft" icon={<Ic.Pin size={12} />}>
          Votre parcelle
        </Pill>
      </div>

      {/* Top-right: price legend. A 4-dot scale keyed to the bucket palette. */}
      <div className="absolute top-3 right-3 flex flex-col gap-1 bg-white/92 backdrop-blur rounded-lg px-3 py-2 shadow-pop">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">
          €/m² vs. médiane
        </div>
        <LegendRow bucket="low" />
        <LegendRow bucket="mid-low" />
        <LegendRow bucket="mid-high" />
        <LegendRow bucket="high" />
      </div>

      {/* Bottom-left: selection card */}
      {selectedId && (
        <SelectionCard
          view={txViews.find((v) => v.tx.id === selectedId)!}
          onClose={() => setSelectedId(null)}
        />
      )}

      {/* Loading / fallback status */}
      {geomsState.status === "loading" && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 text-[10.5px] font-medium text-white bg-black/55 px-2.5 py-1 rounded-full">
          Chargement des parcelles…
        </div>
      )}
      {geomsState.status === "ready" && fallbackCount > 0 && (
        <div className="absolute bottom-3 left-3 text-[10px] text-white/85 bg-black/55 px-2 py-0.5 rounded">
          {fallbackCount} transaction{fallbackCount > 1 ? "s" : ""} affichée
          {fallbackCount > 1 ? "s" : ""} sans contour parcellaire
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function LegendRow({ bucket }: { bucket: PriceBucket }) {
  const s = BUCKET_STYLE[bucket];
  return (
    <div className="flex items-center gap-2 text-[11px] text-ink-800">
      <span
        className="inline-block w-3 h-3 rounded-sm"
        style={{ backgroundColor: s.fill, border: `1.5px solid ${s.stroke}` }}
      />
      {s.label}
    </div>
  );
}

function SelectionCard({
  view,
  onClose,
}: {
  view: {
    tx: DvfTransaction;
    pricePerM2: number | null;
    bucket: PriceBucket;
  };
  onClose: () => void;
}) {
  const { tx, pricePerM2 } = view;
  return (
    <div className="absolute bottom-3 left-3 right-3 md:right-auto md:max-w-[320px] bg-white rounded-xl2 shadow-pop border border-ink-100 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-500">
            {tx.date.slice(0, 10)} · {tx.distance_m} m
          </div>
          <div className="mt-0.5 text-[13px] font-medium text-ink-900 truncate">
            {toTitleCase(tx.libtypbien)}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 w-6 h-6 rounded-full bg-ink-50 hover:bg-ink-100 grid place-items-center text-ink-500"
          aria-label="Fermer"
        >
          <Ic.Cross size={12} />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[12px]">
        <SelectionStat label="Prix" value={formatEur(tx.valeur_eur)} />
        <SelectionStat
          label="Surface"
          value={
            (view.tx.surface_bati_m2 ?? view.tx.surface_terrain_m2)
              ? `${(view.tx.surface_bati_m2 ?? view.tx.surface_terrain_m2)!.toLocaleString("fr-FR")} m²`
              : "—"
          }
        />
        <SelectionStat
          label="€/m²"
          value={pricePerM2 ? `${pricePerM2.toLocaleString("fr-FR")} €` : "—"}
        />
      </div>
    </div>
  );
}

function SelectionStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-500">
        {label}
      </div>
      <div className="mt-0.5 text-[13px] font-semibold tabular text-ink-900">
        {value}
      </div>
    </div>
  );
}

// ─── Geometry helpers ────────────────────────────────────────────────────

function bboxOfAll(
  geoms: AnyGeom[],
  points: Array<[number, number]>,
): Bbox | null {
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  const visit = (ring: number[][]) => {
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  };
  for (const g of geoms) {
    if (!g) continue;
    if (g.type === "Polygon") g.coordinates.forEach(visit);
    else if (g.type === "MultiPolygon")
      g.coordinates.forEach((p) => p.forEach(visit));
  }
  for (const [lng, lat] of points) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  if (!Number.isFinite(minLng)) return null;
  return { minLng, minLat, maxLng, maxLat };
}

function expandBbox(bb: Bbox, meters: number): Bbox {
  const dLat = meters / 111_320;
  const centerLat = (bb.minLat + bb.maxLat) / 2;
  const dLng = meters / (111_320 * Math.cos((centerLat * Math.PI) / 180));
  return {
    minLng: bb.minLng - dLng,
    maxLng: bb.maxLng + dLng,
    minLat: bb.minLat - dLat,
    maxLat: bb.maxLat + dLat,
  };
}

function fitToMetricRatio(bb: Bbox, targetRatio: number): Bbox {
  const centerLat = (bb.minLat + bb.maxLat) / 2;
  const cosLat = Math.cos((centerLat * Math.PI) / 180);
  const dyDeg = bb.maxLat - bb.minLat;
  const dxMetric = (bb.maxLng - bb.minLng) * cosLat;
  const currentRatio = dxMetric / (dyDeg || Number.EPSILON);
  let { minLng, maxLng, minLat, maxLat } = bb;
  if (currentRatio < targetRatio) {
    const targetDxMetric = dyDeg * targetRatio;
    const addMetric = targetDxMetric - dxMetric;
    const addLng = addMetric / 2 / cosLat;
    minLng -= addLng;
    maxLng += addLng;
  } else if (currentRatio > targetRatio) {
    const targetDy = dxMetric / targetRatio;
    const addLat = (targetDy - dyDeg) / 2;
    minLat -= addLat;
    maxLat += addLat;
  }
  return { minLng, maxLng, minLat, maxLat };
}

function geomToSvgPath(
  geom: AnyGeom,
  project: (lng: number, lat: number) => [number, number],
): string {
  if (!geom) return "";
  const ringPath = (ring: number[][]) => {
    if (!ring.length) return "";
    const [x0, y0] = project(ring[0][0], ring[0][1]);
    let d = `M${x0.toFixed(2)} ${y0.toFixed(2)}`;
    for (let i = 1; i < ring.length; i++) {
      const [x, y] = project(ring[i][0], ring[i][1]);
      d += ` L${x.toFixed(2)} ${y.toFixed(2)}`;
    }
    return d + " Z";
  };
  if (geom.type === "Polygon") return geom.coordinates.map(ringPath).join(" ");
  if (geom.type === "MultiPolygon")
    return geom.coordinates
      .map((rings) => rings.map(ringPath).join(" "))
      .join(" ");
  return "";
}

// ─── Small formatters (local copies; cheap enough not to import) ────────

function formatEur(n: number): string {
  if (n >= 1_000_000)
    return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 2).replace(/\.00$/, "")} M€`;
  if (n >= 1_000) return `${Math.round(n / 1_000).toLocaleString("fr-FR")} k€`;
  return `${n.toLocaleString("fr-FR")} €`;
}

function toTitleCase(s: string): string {
  return s
    .toLowerCase()
    .replace(/(^|\s|['-])([a-zà-ÿ])/g, (_, sep, ch) => sep + ch.toUpperCase());
}
